<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Props {
	html?: string | null;
	unsafe?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	html: '',
	unsafe: false,
});

const unsafeContainer = ref<HTMLElement | null>(null);

const decodeHtmlEntities = (value: string) => {
	const decoder = document.createElement('textarea');
	decoder.innerHTML = value;

	return decoder.value;
};

const executeScripts = (container: HTMLElement) => {
	const scripts = Array.from(container.querySelectorAll('script'));

	scripts.forEach((script) => {
		const replacement = document.createElement('script');

		Array.from(script.attributes).forEach((attribute) => {
			replacement.setAttribute(attribute.name, attribute.value);
		});

		replacement.textContent = script.textContent ?? '';
		script.parentNode?.replaceChild(replacement, script);
	});
};

const renderUnsafeHtml = async () => {
	if (!props.unsafe) {
		return;
	}

	await nextTick();

	if (!unsafeContainer.value) {
		return;
	}

	unsafeContainer.value.innerHTML = decodeHtmlEntities(props.html ?? '');
	executeScripts(unsafeContainer.value);
};

watch(
	() => [props.html, props.unsafe],
	() => {
		if (props.unsafe) {
			void renderUnsafeHtml();
			return;
		}

		if (unsafeContainer.value) {
			unsafeContainer.value.innerHTML = '';
		}
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	if (unsafeContainer.value) {
		unsafeContainer.value.innerHTML = '';
	}
});

onMounted(() => {
	if (props.unsafe) {
		void renderUnsafeHtml();
	}
});
</script>

<template>
	<div v-if="!unsafe" v-dompurify-html="html"></div>
	<div v-else ref="unsafeContainer"></div>
</template>
